import classNames from 'classnames';
import { NavLink, NavLinkProps, useLocation, useNavigate } from 'react-router-dom';

import { Logo } from '@client/toolkit';
import { PermissionKeys } from '@client/api';

import { AppProfileMenu } from './app-profile-menu';
import { Profile } from './profile';

import { useTranslation } from 'react-i18next';
import { config } from '../../../config';
import { AgreementsPaths } from '../../../pods/agreements/agreements.router';
import { DashboardPaths } from '../../../pods/dashboard/dashboard.router';
import { PortfolioPaths } from '../../../pods/portfolio/portfolio.router';
import { ReportingPaths } from '../../../pods/reporting/reporting.router';
import { EconomyPaths } from '../../../pods/economy';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { selectSpecificTenant } from '../../../redux';
import { CompaniesPaths } from '../../../pods/companies/companies.router';
import { ProductsPaths } from '../../../pods/products/products.router';
import { TenantsSelectionDropdown } from '../../../components/tenants-selection-dropdown/tenants-selection-dropdown';
import { HasUserPermission } from '../../../components/permission/has-user-permission';
import { useUserPermission } from '../../../hooks/useUserPermission';
import { packageRulesConfig, useUserPackages } from '../../../hooks/useUserPackages/useUserPackages';

type NavigationItemProps = {
  label: string;
  path: NavLinkProps['to'];
};

function NavigationItem({ label, path }: NavigationItemProps) {
  return (
    <NavLink to={path} className="group">
      {({ isActive }) => (
        <div className="flex items-center relative cursor-pointer justify-center h-16 hover:bg-menu-item-hover px-6">
          <div>{label}</div>
          <div
            className={classNames(
              !isActive && 'hidden',
              'absolute bottom-[0rem] w-4/5 bg-brand-primary overflow-hidden rounded-t-xl h-1',
            )}
          ></div>
        </div>
      )}
    </NavLink>
  );
}

export function AppNavbar() {
  const { t: tGlobal } = useTranslation('global');
  const { search } = useLocation();
  const navigate = useNavigate();
  const { hasAnyPermission } = useUserPermission();
  const { getPackagesWithTier } = useUserPackages();
  // Economy figures are per-tenant, so the section is meaningless across "all tenants".
  const specificTenant = useAppSelector(selectSpecificTenant);

  const hasDashboardAccess = hasAnyPermission([PermissionKeys.User.DashboardView, PermissionKeys.Tenant.DashboardView]);
  const hasManagementPackage = getPackagesWithTier(packageRulesConfig.management);

  return (
    <nav className="flex bg-surface-base/50 bg-opacity-50 text-zinc-200 text-sm items-center z-10 absolute top-0 left-0 right-0 h-16 backdrop-blur-md">
      {/* LOGO */}
      <div className="px-4 cursor-pointer" onClick={() => navigate('/')}>
        <Logo className="w-8 h-8" />
      </div>

      {/* NAVIGATION */}
      <div className="flex-grow flex font-medium">
        {hasDashboardAccess ? (
          <NavigationItem label={tGlobal('navigation.navbar.dashboard')} path={DashboardPaths.Dashboard + search} />
        ) : (
          <div className="flex items-center relative justify-center h-16 px-6 opacity-50 cursor-not-allowed select-none"
            title={tGlobal('navigation.navbar.dashboardUnderDevelopment')}
          >
            <div>{tGlobal('navigation.navbar.dashboard')}</div>
          </div>
        )}
        <HasUserPermission permissions={[PermissionKeys.User.ReportView, PermissionKeys.Tenant.ReportView]} partial>
          <NavigationItem label={tGlobal('navigation.navbar.reporting')} path={ReportingPaths.Home + search} />
        </HasUserPermission>
        {/* Demo pages built on mock data — no permission gate yet, but tenant-scoped. */}
        {specificTenant != null && (
          <NavigationItem label={tGlobal('navigation.navbar.economy')} path={EconomyPaths.Home} />
        )}
        <HasUserPermission permissions={[PermissionKeys.Tenant.SpacesList]} partial>
          <NavigationItem label={tGlobal('navigation.navbar.portfolio')} path={PortfolioPaths.Home} />
        </HasUserPermission>
        {hasManagementPackage && (
          <HasUserPermission permissions={[PermissionKeys.Tenant.AgreementsList]} partial>
            <NavigationItem label={tGlobal('navigation.navbar.agreements')} path={AgreementsPaths.AgreementList} />
          </HasUserPermission>
        )}
        {hasManagementPackage && (
          <HasUserPermission permissions={[PermissionKeys.Tenant.ProductsList]} partial>
            <NavigationItem label={tGlobal('navigation.navbar.products')} path={ProductsPaths.Products} />
          </HasUserPermission>
        )}
        <HasUserPermission permissions={[PermissionKeys.Tenant.CompaniesList]} partial>
          <NavigationItem label={tGlobal('navigation.navbar.companies')} path={CompaniesPaths.Companies} />
        </HasUserPermission>
        {config.stage === 'local' && <NavigationItem label="Debug" path="/debug" />}
      </div>

      {/* PROFILE */}
      <div className="flex flex-row items-center gap-2">
        <TenantsSelectionDropdown />
        <AppProfileMenu />
      </div>
      <Profile />
    </nav>
  );
}
